/**
 * Modal "Agregar residente" — extraído de MisDatos.tsx en Sprint 18 P2-B2
 * para reusarlo desde el wizard de onboarding sin duplicar el formulario.
 *
 * Crea Persona + Residente atómicamente en estado pendiente_validacion
 * (POST /me/residentes). El residente NO es el titular (doctrina P18-7):
 * el wizard tampoco precarga al titular aquí.
 */
import { useState, type FormEvent } from 'react';

import { ApiError } from '../../lib/api/client';
import {
  agregarResidenteConPersonaNueva,
  type ParentescoLiteral,
} from '../../lib/api/residentes';
import { PARENTESCO_LABEL } from '../../lib/labels';
import { hoy } from '../../lib/tiempo';
import { Button } from '../ui/Button';
import { InputCelular } from '../ui/InputCelular';
import { InputFecha } from '../ui/InputFecha';
import { InputRut, type RutValidez } from '../ui/InputRut';
import { Modal } from '../ui/Modal';

interface ModalAgregarResidenteProps {
  loteId: string;
  onCerrar: () => void;
  onAgregado: () => void;
  /** H15: si la casa tiene llaveros sin asignar en la bolsa, NO se ofrece
   *  pedir uno nuevo (D-P2-1: solicitud bloqueada si hay bolsa) — el vecino
   *  asigna desde la bolsa. Default false (sin bolsa → se puede pedir). */
  hayLlaverosEnBolsa?: boolean;
}

export function ModalAgregarResidente({
  loteId,
  onCerrar,
  onAgregado,
  hayLlaverosEnBolsa = false,
}: ModalAgregarResidenteProps) {
  const [nombre, setNombre] = useState('');
  const [rut, setRut] = useState('');
  const [rutValidez, setRutValidez] = useState<RutValidez>({
    valid: false,
    canonico: null,
  });
  const [email, setEmail] = useState('');
  const [celular, setCelular] = useState('');
  const [parentesco, setParentesco] = useState<ParentescoLiteral>('conyuge');
  const [desde, setDesde] = useState(hoy());
  // Sprint 17 S17-17 — checkbox "¿necesita llavero?" al pie del modal.
  const [necesitaLlavero, setNecesitaLlavero] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // F4: el RUT es OBLIGATORIO y debe ser válido (mod11).
  //
  // Se lleva la LISTA de lo que falta y no un booleano: un botón gris sin
  // decir por qué obliga a cazar el campo. Ver el mismo patrón en
  // `ModalEditarResidente` (MisDatos), donde nos costó un caso real.
  const faltantes: string[] = [];
  if (nombre.trim().length === 0) faltantes.push('el nombre');
  if (!rutValidez.valid) faltantes.push('un RUT válido');
  if (desde === '') faltantes.push('la fecha "desde"');
  const valido = faltantes.length === 0;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valido || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await agregarResidenteConPersonaNueva({
        nombre_completo: nombre.trim(),
        rut: rutValidez.canonico,
        email: email.trim() || null,
        celular: celular.trim() || null,
        lote_id: loteId,
        parentesco,
        desde,
        // H15: si hay bolsa, nunca se pide (el checkbox no se muestra).
        necesita_llavero: hayLlaverosEnBolsa ? false : necesitaLlavero,
      });
      onAgregado();
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Agregar residente"
      onClose={onCerrar}
      footer={
        <>
          {/* Por qué el botón está gris. */}
          {faltantes.length > 0 && (
            <span className="text-xs text-text-muted mr-auto">
              Falta {faltantes.join(', ')}.
            </span>
          )}
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="form-agregar-residente"
            disabled={!valido || submitting}
          >
            {submitting ? 'Enviando…' : 'Enviar solicitud'}
          </Button>
        </>
      }
    >
      <form
        id="form-agregar-residente"
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <div className="bg-bg-elevated border-l-4 border-orange rounded-sm p-3 text-xs text-text-secondary">
          El residente quedará en estado{' '}
          <strong className="text-text-primary">pendiente de validación</strong>
          {' '}hasta que la Comisión lo apruebe.
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Nombre completo <span className="text-state-danger">*</span>
          </span>
          <input
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            maxLength={200}
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            required
          />
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              RUT <span className="text-state-danger">*</span>
            </span>
            <InputRut
              value={rut}
              onChange={(f, v) => {
                setRut(f);
                setRutValidez(v);
              }}
              aria-label="RUT del residente"
            />
          </label>
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              Parentesco <span className="text-state-danger">*</span>
            </span>
            <select
              value={parentesco}
              onChange={(e) =>
                setParentesco(e.target.value as ParentescoLiteral)
              }
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            >
              {(Object.keys(PARENTESCO_LABEL) as ParentescoLiteral[]).map(
                (p) => (
                  <option key={p} value={p}>{PARENTESCO_LABEL[p]}</option>
                ),
              )}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              Email <span className="text-text-muted">(opcional)</span>
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={255}
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            />
          </label>
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              Celular <span className="text-text-muted">(opcional)</span>
            </span>
            <InputCelular value={celular} onChange={setCelular} />
          </label>
        </div>

        {/* F4: el celular habilita citofonía (la llamada) y el email habilita
            el acceso WiFi. Sin ellos, el residente queda sin esos servicios. */}
        <p className="text-xs text-text-muted">
          El <strong className="text-text-secondary">celular</strong> es
          necesario para la citofonía (la llamada al departamento) y el{' '}
          <strong className="text-text-secondary">email</strong> para el acceso
          WiFi. Si no los completas, este residente quedará sin esos servicios
          hasta que los agregues.
        </p>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Vive aquí desde <span className="text-state-danger">*</span>
          </span>
          <InputFecha
            value={desde}
            onChange={setDesde}
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            requerido
          />
        </label>

        {/* Sprint 17 S17-17 — checkbox al pie. H15: solo cuando la casa NO
            tiene llaveros sin asignar en la bolsa (si los hay, se asignan
            desde la bolsa, no se piden — D-P2-1). */}
        {hayLlaverosEnBolsa ? (
          <p className="text-xs text-text-muted">
            Tu casa tiene llaveros sin asignar. Después de agregar al
            residente, podrás asignarle uno desde la bolsa (no es necesario
            solicitar uno nuevo).
          </p>
        ) : (
          <label className="flex items-start gap-2 text-sm text-text-primary cursor-pointer">
            <input
              type="checkbox"
              checked={necesitaLlavero}
              onChange={(e) => setNecesitaLlavero(e.target.checked)}
              className="mt-0.5 rounded-sm accent-orange"
            />
            <span>
              <span>¿Este residente necesita un llavero?</span>
              <span className="block text-xs text-text-muted mt-0.5">
                Si marcas, se enviará automáticamente una solicitud de
                llavero a la Comisión junto con el alta del residente.
              </span>
            </span>
          </label>
        )}

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
